<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ $t('api.api') }}
                <template v-if="isLoaded && api.name"> - {{ api.name }} </template>

                <div
                    v-if="isLoaded"
                    class="pull-right"
                >
                    <router-link
                        :to="{ name: 'apis.list' }"
                        class="btn btn-sm btn-default"
                    >
                        <span class="sr-only">{{ $t('api.apisList') }}</span>
                        <span class="hidden-xs">{{ $t('api.apisList') }}</span>
                        <span class="visible-xs">
                            <i class="fa fa-list-ul"></i>
                        </span>
                    </router-link>

                    <a
                        v-if="isLoaded && !isError"
                        :href="`/apis/${api.id}/edit`"
                        class="btn btn-sm btn-primary"
                    >
                        <span class="sr-only">{{ $t('edit') }}</span>
                        <span class="hidden-xs">{{ $t('edit') }}</span>
                        <span class="visible-xs">
                            <i class="fa fa-edit"></i>
                        </span>
                    </a>
                </div>
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div class="row">
                <div class="col-xs-12">
                    <div
                        v-if="!isLoaded"
                        class="box box-primary"
                    >
                        <div class="box-body with-border clearfix">
                            <img
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                            />
                        </div>
                    </div>

                    <template v-else>
                        <div
                            v-if="isError || !api.id"
                            class="box box-primary"
                        >
                            <div class="box-body with-border clearfix">
                                <div class="row">
                                    <div class="col-xs-12">
                                        <p>
                                            {{ $t('messages.api.noApi', { id: apiId }) }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <template v-else>
                            <div class="box box-primary">
                                <div
                                    v-if="!isLoaded"
                                    class="box-body with-border clearfix"
                                >
                                    <img
                                        class="table-row-preloader"
                                        rel="preload"
                                        src="/img/table-row-preloader.gif"
                                    />
                                </div>

                                <template v-else>
                                    <div class="box-header with-border">
                                        <h3 class="box-title">{{ $t('info') }}</h3>

                                        <div class="box-tools pull-right">
                                            <button
                                                class="btn btn-box-tool"
                                                :title="$t('collapse')"
                                                type="button"
                                                @click="isCollapseInfo = !isCollapseInfo"
                                            >
                                                <i
                                                    class="fa"
                                                    :class="{
                                                        'fa-plus': !isCollapseInfo,
                                                        'fa-minus': isCollapseInfo,
                                                    }"
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    <vue-slide-up-down
                                        v-show="isLoaded"
                                        :active="isCollapseInfo"
                                    >
                                        <div class="box-body">
                                            <div class="row">
                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('name') }}:</strong>
                                                    <p>{{ api.name }}</p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('active') }}:</strong>
                                                    <p>
                                                        <ValueYesNo :value="api.active" />
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('createdAt') }}:</strong>
                                                    <p>{{ api.created_at }}</p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('updatedAt') }}:</strong>
                                                    <p>{{ api.updated_at }}</p>
                                                </div>
                                            </div>

                                            <div class="row">
                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('api.provider.provider') }}:</strong>
                                                    <p>
                                                        <a
                                                            v-if="api.provider"
                                                            :href="`/apis/providers/${api.provider.id}`"
                                                        >
                                                            {{ api.provider.name }}
                                                        </a>
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('api.account.account') }}:</strong>
                                                    <p>
                                                        <a
                                                            v-if="api.account"
                                                            :href="`/apis/accounts/${api.account.id}`"
                                                        >
                                                            {{ api.account.name }}
                                                        </a>
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('api.tag.tags') }}:</strong>
                                                    <p>
                                                        <ValueListEmpty
                                                            :list="api.tags"
                                                            block
                                                            href="/apis/tags/"
                                                        />
                                                    </p>
                                                </div>
                                            </div>

                                            <div class="row">
                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('nonUsaAvailable') }}:</strong>
                                                    <p>
                                                        <ValueYesNo :value="api.is_non_usa" />
                                                    </p>
                                                </div>
                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('useSourceNumbers') }}:</strong>
                                                    <p>
                                                        <ValueYesNo :value="isUseNumbers" />
                                                    </p>
                                                </div>
                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('type') }}:</strong>
                                                    <p>{{ campaignTypes[api.campaign_type_id] }}</p>
                                                </div>
                                            </div>
                                            <div class="row">
                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('callbackDomain') }}:</strong>
                                                    <p>
                                                        <code v-if="api.callback_domain">{{ api.callback_domain }}</code>
                                                        <code v-else>{{ $t('default') }}</code>
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('incomingDomain') }}:</strong>
                                                    <p>
                                                        <code v-if="api.incoming_domain">{{ api.incoming_domain }}</code>
                                                        <code v-else>{{ $t('default') }}</code>
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('senderName') }}:</strong>
                                                    <p>
                                                        <ValueEmpty :value="api.sender_name" />
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('sendingRate') }}:</strong>
                                                    <p>
                                                        <BadgeTotal
                                                            :value="api.account.sending_rate"
                                                            color="gray"
                                                            title="Per Second"
                                                        />
                                                    </p>
                                                </div>
                                            </div>

                                            <div
                                                v-if="isUseNumbers"
                                                class="row"
                                            >
                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('sourceNumbersFiles') }}:</strong>
                                                    <p>
                                                        <BadgeTotal
                                                            :value="api.source_number_files_count"
                                                            color="gray"
                                                            :title="$t('total')"
                                                        />

                                                        <BadgeTotal
                                                            :value="api.source_number_files_parsed_count"
                                                            color="green"
                                                            :title="$t('parsed')"
                                                        />

                                                        <BadgeTotal
                                                            :value="api.source_number_files_released_count"
                                                            color="red"
                                                            :title="$t('released')"
                                                        />
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('sourceNumbers') }}:</strong>
                                                    <p>
                                                        <BadgeTotal
                                                            :value="api.source_number_ready_count"
                                                            color="green"
                                                            title="Ready"
                                                        />
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('campaign.dlcCampaigns') }}:</strong>
                                                    <p>
                                                        <BadgeTotal
                                                            :value="api.dlc_campaigns_count"
                                                            color="gray"
                                                            :title="$t('total')"
                                                        />
                                                    </p>
                                                </div>
                                            </div>

                                            <div class="row">
                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('campaign.campaigns') }}:</strong>
                                                    <p>
                                                        <BadgeTotal
                                                            :value="api.campaigns_count"
                                                            color="gray"
                                                            :title="$t('campaign.campaignsCount')"
                                                        />
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('message.messages') }}:</strong>
                                                    <p>
                                                        <img
                                                            v-if="isLoadingStats"
                                                            class="table-row-preloader"
                                                            rel="preload"
                                                            src="/img/table-row-preloader.gif"
                                                        />

                                                        <template v-else>
                                                            <BadgeTotal
                                                                :value="api.messages_count"
                                                                color="gray"
                                                                :title="$t('total')"
                                                            />

                                                            <BadgeTotal
                                                                :value="api.messages_sent"
                                                                color="green"
                                                                :title="$t('sent')"
                                                            />

                                                            <BadgeTotal
                                                                :value="api.messages_failed"
                                                                color="red"
                                                                :title="$t('failed')"
                                                            />

                                                            <BadgeTotal
                                                                :value="api.messages_blacklisted"
                                                                color="black"
                                                                :title="$t('total')"
                                                            />
                                                        </template>
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('clicks') }}:</strong>
                                                    <p>
                                                        <img
                                                            v-if="isLoadingStats"
                                                            class="table-row-preloader"
                                                            rel="preload"
                                                            src="/img/table-row-preloader.gif"
                                                        />

                                                        <template v-else>
                                                            <BadgeTotal
                                                                :value="api.percentage"
                                                                color="light-blue"
                                                                :title="$t('percentage')"
                                                            />

                                                            <BadgeTotal
                                                                :value="api.clicks_count"
                                                                color="aqua-active"
                                                                :title="$t('amount')"
                                                            />
                                                        </template>
                                                    </p>
                                                </div>

                                                <div class="col-xs-12 col-sm-6 col-md-3">
                                                    <strong>{{ $t('useTfn') }}:</strong>
                                                    <p>
                                                        <ValueYesNo :value="Boolean(api.provider.use_tfn && api.use_tfn)" />
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="row">
                                                <div
                                                    class="col-xs-12 col-sm-6 col-md-3"
                                                >
                                                    <strong>{{ $t('usePlus') }}:</strong>
                                                    <p >
                                                        <ValueYesNo
                                                            v-if="api.provider.use_setting_plus"
                                                            :value="Boolean(api.use_plus)"
                                                        />
                                                        <em v-else class="text-info">{{ $t('none') }}</em>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </vue-slide-up-down>
                                </template>
                            </div>

                            <div class="box box-primary">
                                <div class="box-header with-border">
                                    <h3 class="box-title">{{ $t('campaign.dlcCampaigns') }}</h3>

                                    <div class="box-tools pull-right">
                                        <button
                                            class="btn btn-box-tool"
                                            :title="$t('collapse')"
                                            type="button"
                                            @click="isCollapseDlcCampaigns = !isCollapseDlcCampaigns"
                                        >
                                            <i
                                                class="fa"
                                                :class="{
                                                    'fa-plus': !isCollapseDlcCampaigns,
                                                    'fa-minus': isCollapseDlcCampaigns,
                                                }"
                                            />
                                        </button>
                                    </div>
                                </div>

                                <vue-slide-up-down
                                    v-show="isLoaded"
                                    :active="isCollapseDlcCampaigns"
                                >
                                    <DlcCampaignsTable :apiId="apiId" />
                                </vue-slide-up-down>
                            </div>

                            <div class="box box-primary">
                                <div class="box-header with-border">
                                    <h3 class="box-title">{{ $t('sourceNumbersBuyGroups') }}</h3>

                                    <div class="box-tools pull-right">
                                        <a
                                            :href="`/apis/${apiId}/asn_buy_group/new`"
                                            class="btn btn-sm btn-primary"
                                        >
                                            <span class="sr-only">{{ $t('addNew') }}</span>
                                            <span class="hidden-xs">{{ $t('addNew') }}</span>
                                            <span class="visible-xs"><i class="fa fa-plus"></i></span>
                                        </a>

                                        <button
                                            class="btn btn-box-tool"
                                            :title="$t('collapse')"
                                            type="button"
                                            @click="isCollapseAsnBuyGroups = !isCollapseAsnBuyGroups"
                                        >
                                            <i
                                                class="fa"
                                                :class="{
                                                    'fa-plus': !isCollapseAsnBuyGroups,
                                                    'fa-minus': isCollapseAsnBuyGroups,
                                                }"
                                            />
                                        </button>
                                    </div>
                                </div>

                                <vue-slide-up-down
                                    v-show="isLoaded"
                                    :active="isCollapseAsnBuyGroups"
                                >
                                    <AsnBuyGroupsTable
                                        :apiId="apiId"
                                        :isUseGroup="isUseGroups"
                                    />
                                </vue-slide-up-down>
                            </div>

                            <div class="box box-primary">
                                <div class="box-header with-border">
                                    <h3 class="box-title">{{ $t('sourceNumbersReleaseGroups') }}</h3>

                                    <div class="box-tools pull-right">
                                        <a
                                            :href="`/apis/${apiId}/asn_release_group/new`"
                                            class="btn btn-sm btn-primary"
                                        >
                                            <span class="sr-only">{{ $t('addNew') }}</span>
                                            <span class="hidden-xs">{{ $t('addNew') }}</span>
                                            <span class="visible-xs"><i class="fa fa-plus"></i></span>
                                        </a>

                                        <button
                                            class="btn btn-box-tool"
                                            :title="$t('collapse')"
                                            type="button"
                                            @click="isCollapseAsnReleaseGroups = !isCollapseAsnReleaseGroups"
                                        >
                                            <i
                                                class="fa"
                                                :class="{
                                                    'fa-plus': !isCollapseAsnReleaseGroups,
                                                    'fa-minus': isCollapseAsnReleaseGroups,
                                                }"
                                            />
                                        </button>
                                    </div>
                                </div>

                                <vue-slide-up-down
                                    v-show="isLoaded"
                                    :active="isCollapseAsnReleaseGroups"
                                >
                                    <AsnReleaseGroupsTable :apiId="apiId" />
                                </vue-slide-up-down>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import formatsMixin from '@/mixins/formatsMixin';
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import ValueYesNo from '@/components/subcomponents/ValueYesNo';
import ValueEmpty from '@/components/subcomponents/ValueEmpty';
import ValueListEmpty from '@/components/subcomponents/ValueListEmpty';
import DlcCampaignsTable from '@/components/apis/DlcCampaigns/DlcCampaignsTable';
import AsnBuyGroupsTable from '@/components/api-source-numbers/BuyGroupsTable';
import AsnReleaseGroupsTable from '@/components/api-source-numbers/ReleaseGroupsTable';

export default {
    components: {
        BadgeTotal,
        ValueYesNo,
        ValueEmpty,
        ValueListEmpty,
        DlcCampaignsTable,
        AsnBuyGroupsTable,
        AsnReleaseGroupsTable,
    },
    mixins: [formatsMixin],
    data() {
        return {
            isError: false,
            isLoaded: false,
            isLoadingStats: false,
            isCollapseInfo: true,
            isCollapseDlcCampaigns: true,
            isCollapseAsnBuyGroups: true,
            isCollapseAsnReleaseGroups: true,
            apiId: Number(this.$route.params.id),
            api: {},
            countryTypes: {},
            campaignTypes: {},
            isUseNumbers: false,
            isUseGroups: false,
            ids: [],
        };
    },
    computed: {
        headTitle() {
            return this.api.name && `${this.$t('api.api')} ${this.api.name}`;
        },
    },
    created() {
        this.fetchApi();
    },
    methods: {
        async fetchApi() {
            try {
                const {
                    data: { api, countryTypes, campaignTypes, isUseNumbers, isUseGroups, ids },
                } = await axios.get(`/apis/data/${this.apiId}`);

                this.api = api;
                this.countryTypes = countryTypes;
                this.campaignTypes = campaignTypes;
                this.isUseNumbers = isUseNumbers;
                this.isUseGroups = isUseGroups;
                this.ids = ids || [];

                if (this.ids.length) {
                    this.fetchStats(this.ids);
                }
            } catch (error) {
                this.isError = true;

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoaded = true;
            }
        },
        async fetchStats(ids) {
            try {
                this.isLoadingStats = true;

                const {
                    data: { stats },
                } = await axios.get(`/apis/${this.apiId}/stats?ids=${ids}`);

                this.api = {
                    ...this.api,
                    ...stats[0],
                };
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoadingStats = false;
            }
        },
    },
};
</script>
